import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

export default function ImportCsv() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(searchParams.get('group_id') || user?.group_id || '');
  const [mode, setMode] = useState('csv'); // 'csv' | 'pdf'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      api.get('/groups').then((r) => setGroups(r.data));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupId) { setError(T.importCsv.errNoGroup); return; }
    if (!file) { setError(T.importCsv.errNoFile); return; }

    setError('');
    setResult(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('group_id', groupId);

    const endpoint = mode === 'pdf' ? '/transactions/import-pdf' : '/transactions/import-csv';
    formData.append(mode === 'pdf' ? 'pdf_file' : 'csv_file', file);

    try {
      const { data } = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || T.importCsv.errImport);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setFile(null);
    setResult(null);
    setError('');
  };

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-green-700 hover:text-green-900 text-sm">← {T.importCsv.back}</button>
        <h1 className="text-2xl font-bold text-gray-800">{T.importCsv.title}</h1>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 rounded-xl border border-gray-200 bg-white p-1">
        <button type="button" onClick={() => switchMode('csv')}
          className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
            mode === 'csv' ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          📄 CSV
        </button>
        <button type="button" onClick={() => switchMode('pdf')}
          className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
            mode === 'pdf' ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          📕 M-Koba PDF
        </button>
      </div>

      {/* Instructions */}
      {mode === 'csv' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <p className="font-semibold mb-2">{T.importCsv.instructions}</p>
          <ul className="list-disc list-inside space-y-1">
            <li>{T.importCsv.instrStep1}</li>
            <li>{T.importCsv.instrStep2}</li>
            <li>{T.importCsv.instrStep3}</li>
            <li>{T.importCsv.instrStep4}</li>
          </ul>
        </div>
      ) : (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800">
          <p className="font-semibold mb-2">Pakia PDF ya M-Koba Group Statement moja kwa moja</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Pakua statement kutoka Vodacom M-Koba (PDF), kisha ipakie hapa moja kwa moja — hakuna haja ya kuibadilisha kuwa CSV.</li>
            <li>Miamala ya "Deposit/Contribution" itaingizwa kama ada za wanachama.</li>
            <li>Miamala ya "Withdraw/Transfer fund" HAITAINGIZWA moja kwa moja — itaorodheshwa hapa chini ili uiweke mwenyewe kwenye Matumizi (Expenses) ukiridhia.</li>
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        {user?.role === 'superadmin' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{T.importCsv.groupLabel} *</label>
            <select required value={groupId} onChange={(e) => setGroupId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="">{T.importCsv.groupSelect}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{T.importCsv.fileLabel} *</label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
            <input
              type="file"
              accept={mode === 'pdf' ? '.pdf' : '.csv'}
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="import-file-input"
            />
            <label htmlFor="import-file-input" className="cursor-pointer">
              {file ? (
                <div>
                  <div className="text-green-700 text-2xl mb-2">✅</div>
                  <p className="text-sm text-gray-800 font-medium">{file.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <div className="text-gray-400 text-3xl mb-2">{mode === 'pdf' ? '📕' : '📄'}</div>
                  <p className="text-sm text-gray-600">{T.importCsv.fileClick}</p>
                  <p className="text-xs text-gray-400 mt-1">{T.importCsv.fileDrop}</p>
                </div>
              )}
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !file || !groupId}
          className="w-full bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold disabled:opacity-60 transition-colors"
        >
          {loading ? T.importCsv.submitting : `📥 ${T.importCsv.submit}`}
        </button>
      </form>

      {/* Result modal */}
      {result && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <h3 className="font-bold text-green-800 mb-3 text-lg">✅ {T.importCsv.success}</h3>
              <div className="space-y-2 text-sm text-green-700">
                <div>{T.importCsv.imported}: <span className="font-bold text-2xl">{result.imported}</span></div>
                <div>{T.importCsv.skipped}: <span className="font-semibold">{result.skipped}</span></div>
                <p className="text-green-800 font-medium mt-3">{result.message}</p>
              </div>

              {result.withdrawals?.length > 0 && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h4 className="font-semibold text-amber-800 text-sm mb-2">
                    ⚠️ Withdrawals hazikuingizwa moja kwa moja ({new Intl.NumberFormat('sw-TZ').format(result.withdraw_total)} TZS)
                  </h4>
                  <p className="text-xs text-amber-700 mb-3">
                    Hizi hazikuwekwa kama ada ya mwanachama. Kama ni matumizi ya kikundi, ziweke kwenye ukurasa wa Expenses.
                  </p>
                  <div className="space-y-2">
                    {result.withdrawals.map((w, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs">
                        <div>
                          <p className="font-medium text-gray-800">{w.name} — {w.phone}</p>
                          <p className="text-gray-400">{w.date} · {w.ref}</p>
                        </div>
                        <span className="font-bold text-amber-700">
                          {new Intl.NumberFormat('sw-TZ').format(w.amount)} TZS
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setResult(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                {T.common.cancel}
              </button>
              <button
                onClick={() => navigate(`/transactions?group_id=${groupId}`)}
                className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800"
              >
                {T.importCsv.viewTransactions}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
